<template>
  <main class="app-main marketing-board-page" dir="rtl">
    <div class="app-content-header">
      <div class="container-fluid">
        <div class="row align-items-center">
          <div class="col-sm-7">
            <h3 class="mb-0">طلبات المواد التسويقية</h3>
          </div>
          <div class="col-sm-5">
            <ol class="breadcrumb float-sm-end mb-0">
              <li class="breadcrumb-item"><router-link to="/">الرئيسية</router-link></li>
              <li class="breadcrumb-item"><router-link to="/properties">العقارات</router-link></li>
              <li class="breadcrumb-item active" aria-current="page">طلبات المواد التسويقية</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <div class="app-content">
      <div class="container-fluid">
        <errorAlert v-if="hasError" :alertMessage="message" :errorNumber="errorNumber" />

        <section class="board-toolbar">
          <div class="toolbar-top">
            <div class="page-heading">
              <span class="heading-icon"><i class="bi bi-list-check"></i></span>
              <div>
                <h4>لوحة طلبات المواد التسويقية والتعميد والإسناد</h4>
                <p>متابعة طلبات التسويق واللوحات وتعميدها وإسنادها وتحديث حالتها</p>
              </div>
            </div>

            <div class="service-filter">
              <button v-if="canCreate" type="button" class="new-request-btn" @click="openNewRequest">
                <i class="bi bi-plus-lg"></i>
                إضافة طلب تسويقي
              </button>
              <label for="marketingService">تصفية حسب الخدمة:</label>
              <div class="select-wrap">
                <select id="marketingService" v-model="selectedService" class="form-select" @change="changeService">
                  <option value="all">جميع خدمات التسويق</option>
                  <option value="banner">اللوحات الإعلانية</option>
                  <option value="photography">التصوير الاحترافي</option>
                  <option value="social">النشر على السوشيال ميديا</option>
                </select>
                <i class="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>

          <form class="board-filters" @submit.prevent="applyFilters">
            <div class="filter-field">
              <label for="requestNumber">رقم الطلب</label>
              <input id="requestNumber" v-model.trim="requestNumber" type="text" class="form-control" placeholder="مثال: 125 أو ORD-2026-125" />
            </div>
            <div class="filter-field">
              <label for="employeeName">اسم الموظف</label>
              <input id="employeeName" v-model.trim="employeeName" type="text" class="form-control" placeholder="طالب اللوحة أو مسؤول التعميد أو مسؤول التنفيذ" />
            </div>
            <div class="filter-actions">
              <button type="submit" class="btn btn-success"><i class="bi bi-search"></i> بحث</button>
              <button type="button" class="btn btn-light" @click="resetFilters">مسح</button>
            </div>
          </form>

          <div class="status-tabs" role="tablist" aria-label="تصفية الطلبات حسب الحالة">
            <button
              v-for="tab in statusTabs"
              :key="tab.key"
              type="button"
              class="status-tab"
              :class="{ active: selectedStatus === tab.key }"
              :aria-selected="selectedStatus === tab.key"
              @click="changeStatus(tab.key)"
            >
              <span v-if="tab.key === 'pending_approval' && tab.count" class="tab-count tab-count--warning">{{ tab.count }}</span>
              {{ tab.label }}
              <strong v-if="tab.key === 'all'">({{ tab.count }})</strong>
            </button>
          </div>
        </section>

        <section class="requests-card">
          <mainLoader v-if="mainLoading" />

          <div v-else-if="rows.length" class="table-responsive">
            <table class="requests-table">
              <thead>
                <tr>
                  <th>رقم الطلب</th>
                  <th>العقار / المدينة / الحي</th>
                  <th>نوع الطلب والخدمة</th>
                  <th>تفاصيل الطلب</th>
                  <th>الترخيص / الموقع</th>
                  <th>الموظفون</th>
                  <th>حالة الطلب والتعميد</th>
                  <th>الإجراء والتعميد والإسناد</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="`${row.service}-${row.id}`">
                  <td class="order-cell">
                    <button type="button" class="order-number" @click="openDetails(row)">
                      {{ orderNumber(row) }}
                    </button>
                    <small>{{ formatDate(row.createdAt) }}</small>
                  </td>

                  <td>
                    <strong>{{ propertyTitle(row) }}</strong>
                    <span class="cell-subtitle">#{{ propertyOf(row)?.id || '—' }}</span>
                    <small>{{ propertyLocation(row) }}</small>
                    <small>المساحة: {{ propertyArea(row) }}</small>
                    <small>الحارس: {{ row.guardNumber || 'غير محدد' }}</small>
                  </td>

                  <td>
                    <div class="service-name">
                      <i :class="serviceIcon(row)"></i>
                      <strong>{{ requestTypeLabel(row) }}</strong>
                    </div>
                    <small>{{ row.requestSource === 'app' ? 'طلب من التطبيق' : 'طلب من لوحة التحكم' }}</small>
                  </td>

                  <td>
                    <strong>{{ materialDetails(row) }}</strong>
                    <small>{{ purposeChoices[row.purpose] || row.purpose || 'الغرض غير محدد' }}</small>
                    <small class="request-note"><b>الملاحظات:</b> {{ row.adminNote || row.notes || 'غير محددة' }}</small>
                    <small v-if="row.approvalNote" class="request-note"><b>التعميد:</b> {{ row.approvalNote }}</small>
                  </td>

                  <td>
                    <span class="license-number">ترخيص: {{ adLicense(row) }}</span>
                    <router-link v-if="propertyOf(row)?.id" :to="`/properties/update/${propertyOf(row).id}`" class="location-link">
                      <i class="bi bi-geo-alt-fill"></i>
                      فتح في الداشبورد
                    </router-link>
                    <br>
                    <a v-if="propertyMapUrl(row)" :href="propertyMapUrl(row)" target="_blank" rel="noopener noreferrer" class="location-link location-link--map">
                      <i class="bi bi-map-fill"></i>
                      فتح الموقع على الخريطة
                    </a>
                    <small v-else class="location-unavailable"><i class="bi bi-geo-alt"></i> الإحداثيات غير محددة</small>
                  </td>

                  <td>
                    <small class="employee-line"><b>الطالب:</b> {{ row.RequestedBy?.name || (row.requestSource === 'app' ? 'التطبيق' : 'غير محدد') }}</small>
                    <small class="employee-line"><b>مسؤول التعميد:</b> {{ row.ApprovalManager?.name || 'غير محدد' }}</small>
                    <small class="employee-line"><b>مسؤول التنفيذ:</b> {{ row.AssignedAdmin?.name || 'بانتظار الإسناد' }}</small>
                    <small v-if="row.assignedAt">{{ formatDate(row.assignedAt) }}</small>
                  </td>

                  <td>
                    <span :class="['request-status', `request-status--${requestStatusClass(row)}`]">
                      <i :class="statusIcon(requestStatusClass(row))"></i>
                      {{ requestStatusLabel(row) }}
                    </span>
                  </td>

                  <td class="action-cell">
                    <div v-if="actionLoadingId === row.id || executionSheetLoadingId === row.id" class="action-loading">
                      <span class="spinner-border spinner-border-sm"></span>
                      {{ executionSheetLoadingId === row.id ? 'جاري إعداد PDF' : 'جاري التحديث' }}
                    </div>

                    <template v-else-if="canUpdate || canDelete || hasWorkflowAccess || executionSheetAvailable(row)">
                      <div class="action-stack">
                        <button
                          v-if="row.status === 'pending_approval' && canApproveRow(row)"
                          type="button"
                          class="workflow-btn workflow-btn--warning"
                          @click="approveAndAssign(row)"
                        >
                          <i class="bi bi-check2-circle"></i>
                          تعميد وإسناد
                        </button>

                        <button
                          v-else-if="row.status === 'approved' && canApproveRow(row)"
                          type="button"
                          class="workflow-btn workflow-btn--primary"
                          @click="assignRequest(row)"
                        >
                          <i class="bi bi-person-check-fill"></i>
                          إسناد موظف
                        </button>

                        <button
                          v-if="row.status === 'assigned' && canExecuteRow(row)"
                          type="button"
                          class="workflow-btn workflow-btn--primary"
                          @click="startRequest(row)"
                        >
                          <i class="bi bi-play-circle-fill"></i>
                          بدء التنفيذ
                        </button>

                        <button
                          v-if="row.status === 'in_progress' && canExecuteRow(row)"
                          type="button"
                          class="workflow-btn workflow-btn--success"
                          @click="openCompletion(row)"
                        >
                          <i class="bi bi-check-circle-fill"></i>
                          {{ row.service === 'banner' ? 'إكمال ورفع الصور' : 'تأكيد اكتمال التنفيذ' }}
                        </button>

                        <a
                          v-if="row.status === 'completed' && bannerImage(row)"
                          :href="MEDIA_URL + bannerImage(row)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="completed-link"
                        >
                          <i class="bi bi-image-fill"></i>
                          عرض صورة التنفيذ
                        </a>

                        <span v-if="row.status === 'completed' && !bannerImage(row)" class="completed-link completed-link--plain">
                          <i class="bi bi-check-circle-fill"></i>
                          تم التنفيذ
                        </span>

                        <span v-if="['rejected', 'cancelled'].includes(row.status)" class="closed-request">
                          <i class="bi bi-info-circle"></i>
                          {{ row.rejectionReason || 'لا توجد إجراءات متاحة' }}
                        </span>

                        <button
                          v-if="executionSheetAvailable(row)"
                          type="button"
                          class="workflow-btn workflow-btn--pdf"
                          @click="openExecutionSheet(row)"
                        >
                          <i class="bi bi-file-earmark-pdf-fill"></i>
                          نموذج التنفيذ PDF
                        </button>

                        <div v-if="hasSecondaryActions(row)" class="secondary-actions">
                          <button
                            v-if="['assigned', 'in_progress'].includes(row.status) && canApproveRow(row)"
                            type="button"
                            class="icon-action icon-action--primary"
                            title="تعديل إسناد الطلب"
                            @click="reassignRequest(row)"
                          >
                            <i class="bi bi-person-gear"></i>
                            <span>تعديل الإسناد</span>
                          </button>
                          <button
                            v-if="row.status === 'pending_approval' && canUpdate"
                            type="button"
                            class="icon-action icon-action--primary"
                            title="تعديل الطلب"
                            @click="openDetails(row)"
                          >
                            <i class="bi bi-pencil"></i>
                            <span>تعديل</span>
                          </button>
                          <button
                            v-if="canDelete"
                            type="button"
                            class="icon-action icon-action--danger"
                            title="حذف الطلب"
                            @click="deleteRequest(row)"
                          >
                            <i class="bi bi-trash"></i>
                            <span>حذف</span>
                          </button>
                        </div>
                      </div>
                    </template>

                    <button v-else type="button" class="details-btn" @click="openDetails(row)">
                      <i class="bi bi-eye"></i>
                      عرض التفاصيل
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="empty-state">
            <span><i class="bi bi-inbox"></i></span>
            <h5>لا توجد طلبات مطابقة</h5>
            <p>جرّب تغيير حالة الطلب أو نوع الخدمة المختار.</p>
          </div>
        </section>

        <nav v-if="pages.length > 1" class="board-pagination" aria-label="صفحات الطلبات">
          <button type="button" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            v-for="page in pages"
            :key="page"
            type="button"
            :class="{ active: currentPage === page }"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>
          <button type="button" :disabled="currentPage === pages.length" @click="goToPage(currentPage + 1)">
            <i class="bi bi-chevron-left"></i>
          </button>
        </nav>
      </div>
    </div>

    <div v-if="completionRequest" class="board-modal-backdrop" @click.self="closeCompletion">
      <section class="board-modal" role="dialog" aria-modal="true" aria-labelledby="completionTitle">
        <div class="board-modal__header">
          <div>
            <h5 id="completionTitle">إكمال {{ requestTypeLabel(completionRequest) }}</h5>
            <p>الطلب {{ orderNumber(completionRequest) }}</p>
          </div>
          <button type="button" class="btn-close" aria-label="إغلاق" @click="closeCompletion"></button>
        </div>

        <div v-if="completionRequest.requestType === 'removal'" class="mb-3">
          <label class="form-label">تاريخ الإزالة</label>
          <input v-model="completionForm.removedAt" type="date" class="form-control" />
        </div>

        <div v-else class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">رقم اللوحة</label>
            <input v-model="completionForm.bannerNumber" type="text" class="form-control" placeholder="يُنشأ تلقائياً عند تركه فارغاً" />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">تاريخ التركيب</label>
            <input v-model="completionForm.installedAt" type="date" class="form-control" />
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label">
            {{ completionRequest.requestType === 'removal' ? 'صورة إزالة اللوحة' : 'صورة تنفيذ اللوحة' }}
            <span class="text-danger">*</span>
          </label>
          <input ref="completionImage" type="file" accept=".png,.jpg,.jpeg,.webp" class="form-control" @change="setCompletionImage" />
          <small class="text-muted">تُحفظ الصورة داخل سجل هذا الطلب ولا تضيع عند تنفيذ تحديث أو إزالة لاحقة.</small>
        </div>

        <div class="board-modal__actions">
          <button type="button" class="btn btn-light" :disabled="completionLoading" @click="closeCompletion">إلغاء</button>
          <button type="button" class="btn btn-success" :disabled="completionLoading" @click="completeRequest">
            <span v-if="completionLoading" class="spinner-border spinner-border-sm ms-1"></span>
            تأكيد الإكمال
          </button>
        </div>
      </section>
    </div>

    <div v-if="detailsPropertyId" class="details-backdrop" @click.self="closeDetails">
      <aside class="details-panel" aria-label="تفاصيل طلبات العقار">
        <div class="details-panel__header">
          <div>
            <h5>تفاصيل المواد التسويقية للعقار #{{ detailsPropertyId }}</h5>
            <p>سجل الطلبات واللوحة الحالية</p>
          </div>
          <button type="button" class="btn-close" aria-label="إغلاق" @click="closeDetails"></button>
        </div>
        <div class="details-panel__body">
          <PropertyMarketingMaterials
            :key="detailsPropertyId"
            :property-id="detailsPropertyId"
            :read-only="!canCreate && !canUpdate && !canDelete && !hasWorkflowAccess"
          />
        </div>
      </aside>
    </div>
  </main>
</template>

<script>
import { mapGetters } from 'vuex';
import { checkAccess, toast } from '@/helpers/tools';
import PropertyMarketingMaterials from '@/components/PropertyMarketingMaterials.vue';

const EMPTY_COMPLETION_FORM = () => ({
  bannerNumber: '',
  installedAt: '',
  removedAt: '',
  image: null,
});

export default {
  name: 'PropertyMarketingMaterialsList',
  components: { PropertyMarketingMaterials },
  data() {
    const allowedStatuses = ['all', 'pending_approval', 'approved', 'in_progress', 'completed', 'removed'];
    const routeStatus = allowedStatuses.includes(this.$route.query.status) ? this.$route.query.status : 'all';
    return {
      mainLoading: true,
      hasError: false,
      message: '',
      errorNumber: 0,
      rows: [],
      pages: [],
      currentPage: Math.max(parseInt(this.$route.query.page) || 1, 1),
      selectedStatus: routeStatus,
      selectedService: this.$route.query.service || 'all',
      statusCounts: {},
      requestStatusChoices: {},
      requestTypeChoices: {},
      materialChoices: {},
      purposeChoices: {},
      adminsList: [],
      workflowPermissions: {
        canApprove: false,
        canExecute: false,
        isSuperUser: false,
        currentAdminId: null,
      },
      actionLoadingId: null,
      executionSheetLoadingId: null,
      completionRequest: null,
      completionLoading: false,
      completionForm: EMPTY_COMPLETION_FORM(),
      detailsPropertyId: null,
      requestNumber: this.$route.query.requestNumber || this.$route.query.requestId || '',
      employeeName: this.$route.query.employeeName || '',
    };
  },
  computed: {
    ...mapGetters(['MEDIA_URL']),
    canCreate() {
      return checkAccess('property_banner_requests_create');
    },
    canUpdate() {
      return checkAccess('property_banner_requests_update');
    },
    canDelete() {
      return checkAccess('property_banner_requests_delete');
    },
    hasWorkflowAccess() {
      return this.workflowPermissions.canApprove || this.workflowPermissions.canExecute;
    },
    statusTabs() {
      return [
        { key: 'all', label: 'الكل', count: this.statusCounts.all || 0 },
        { key: 'pending_approval', label: 'بانتظار التعميد', count: this.statusCounts.pending_approval || 0 },
        { key: 'approved', label: 'معتمد / بانتظار الإسناد', count: this.statusCounts.approved || 0 },
        { key: 'in_progress', label: 'قيد التنفيذ', count: (this.statusCounts.assigned || 0) + (this.statusCounts.in_progress || 0) },
        { key: 'completed', label: 'تم التركيب / التنفيذ', count: this.statusCounts.completed || 0 },
        { key: 'removed', label: 'لوحات مُزالة', count: this.statusCounts.removed || 0 },
      ];
    },
  },
  methods: {
    getList(showLoader = true) {
      if(showLoader) this.mainLoading = true;
      this.hasError = false;
      const params = {
        page: this.currentPage,
        status: this.selectedStatus,
        service: this.selectedService,
        ...(this.requestNumber ? { requestNumber: this.requestNumber } : {}),
        ...(this.employeeName ? { employeeName: this.employeeName } : {}),
      };
      return this.$http.get('/api/admin/propertyBannerRequests/list', { params }).then(({ data }) => {
        this.rows = data.rows || [];
        this.pages = data.pages || [];
        this.statusCounts = data.statusCounts || {};
        this.requestStatusChoices = data.requestStatusChoices || {};
        this.requestTypeChoices = data.requestTypeChoices || {};
        this.materialChoices = data.materialChoices || {};
        this.purposeChoices = data.purposeChoices || {};
        this.adminsList = data.adminsList || [];
        this.workflowPermissions = data.workflowPermissions || this.workflowPermissions;
      }).catch(err => {
        [this.message, this.errorNumber] = this.$globalError(err, true);
        this.hasError = true;
      }).finally(() => {
        this.mainLoading = false;
      });
    },
    syncRoute() {
      const query = {};
      if(this.currentPage > 1) query.page = this.currentPage;
      if(this.selectedStatus !== 'all') query.status = this.selectedStatus;
      if(this.selectedService !== 'all') query.service = this.selectedService;
      if(this.requestNumber) query.requestNumber = this.requestNumber;
      if(this.employeeName) query.employeeName = this.employeeName;
      this.$router.replace({ query }).catch(() => {});
    },
    applyFilters() {
      this.currentPage = 1;
      this.syncRoute();
      this.getList();
    },
    resetFilters() {
      this.requestNumber = '';
      this.employeeName = '';
      this.currentPage = 1;
      this.syncRoute();
      this.getList();
    },
    async openNewRequest() {
      if(!this.canCreate) return;
      const result = await this.$swal.fire({
        title: 'إضافة طلب تسويقي',
        input: 'number',
        inputLabel: 'رقم العقار',
        inputPlaceholder: 'اكتب رقم العقار',
        inputAttributes: { min: 1, step: 1 },
        inputValidator: value => !value || Number(value) < 1 ? 'رقم العقار مطلوب' : undefined,
        showCancelButton: true,
        confirmButtonText: 'فتح بيانات العقار',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#176548',
      });
      if(result.isConfirmed) this.detailsPropertyId = Number(result.value);
    },
    changeStatus(status) {
      if(this.selectedStatus === status) return;
      this.selectedStatus = status;
      this.currentPage = 1;
      this.syncRoute();
      this.getList();
    },
    changeService() {
      this.currentPage = 1;
      this.syncRoute();
      this.getList();
    },
    goToPage(page) {
      if(page < 1 || page === this.currentPage || page > this.pages.length) return;
      this.currentPage = page;
      this.syncRoute();
      this.getList();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    propertyOf(row) {
      return row?.Property || row?.Properties || null;
    },
    bannerOf(row) {
      return row?.PropertyBanner || row?.PropertyBanners || null;
    },
    propertyTitle(row) {
      const property = this.propertyOf(row);
      if(!property) return 'عقار غير محدد';
      const type = property.PropertyType?.name;
      return property.title || (type ? `${type} - عقار #${property.id}` : `عقار #${property.id}`);
    },
    propertyLocation(row) {
      const property = this.propertyOf(row);
      if(!property) return 'الموقع غير محدد';
      const city = property.City?.name;
      const neighborhood = property.Neighborhood?.name;
      return [city, neighborhood].filter(Boolean).join(' - ') || property.Region?.name || 'الموقع غير محدد';
    },
    propertyArea(row) {
      const area = row?.propertyArea || this.propertyOf(row)?.area;
      return area ? `${area} م²` : 'غير محددة';
    },
    propertyMapUrl(row) {
      if(typeof row?.mapUrl === 'string' && row.mapUrl.startsWith('https://www.google.com/maps'))
        return row.mapUrl;
      const property = this.propertyOf(row);
      const latitude = Number(property?.latitude);
      const longitude = Number(property?.longitude);
      if(!Number.isFinite(latitude) || !Number.isFinite(longitude) || latitude === 0 || longitude === 0)
        return '';
      return `https://www.google.com/maps?q=${latitude},${longitude}`;
    },
    adLicense(row) {
      const property = this.propertyOf(row);
      return property?.AdValidator?.advertisement?.adLicenseNumber || property?.PropertyOwner?.adLicenseNumber || 'غير محدد';
    },
    requestTypeLabel(row) {
      if(row?.service === 'photography') return 'تصوير العقار';
      if(row?.service === 'social') return 'نشر للسوشيال ميديا';
      return this.requestTypeChoices[row?.requestType] || {
        installation: 'تركيب لوحة',
        update: 'تحديث لوحة',
        removal: 'إزالة لوحة',
      }[row?.requestType] || 'طلب تسويقي';
    },
    serviceIcon(row) {
      if(row?.service === 'photography') return 'bi bi-camera-fill text-primary';
      if(row?.service === 'social') return 'bi bi-share-fill text-purple';
      return row?.requestType === 'removal' ? 'bi bi-sign-turn-slight-left-fill text-danger' : 'bi bi-pin-angle-fill text-success';
    },
    materialDetails(row) {
      if(row?.service === 'photography')
        return [row.optionName, row.shootingDate ? `التصوير: ${this.formatDate(row.shootingDate)}` : null].filter(Boolean).join(' · ');
      if(row?.service === 'social')
        return [row.optionName, row.platforms?.length ? `المنصات: ${row.platforms.map(value => this.platformLabel(value)).join('، ')}` : null].filter(Boolean).join(' · ');
      const material = this.materialChoices[row.material] || row.material;
      const size = row.size || row.PropertyBannerSize?.name;
      return [material, size].filter(Boolean).join(' · ') || 'تفاصيل اللوحة غير محددة';
    },
    orderNumber(row) {
      const year = row?.createdAt ? new Date(row.createdAt).getFullYear() : new Date().getFullYear();
      return `${row?.service === 'banner' ? 'ORD' : 'MKT'}-${year}-${String(row?.id || 0).padStart(3, '0')}`;
    },
    platformLabel(value) {
      return {x: 'X', instagram: 'إنستغرام', snapchat: 'سناب شات', tiktok: 'تيك توك'}[value] || value;
    },
    formatDate(value) {
      if(!value) return '';
      const date = new Date(value);
      if(Number.isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat('ar-SA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
    },
    statusIcon(status) {
      return {
        pending_approval: 'bi bi-clock-fill',
        approved: 'bi bi-patch-check-fill',
        assigned: 'bi bi-person-check-fill',
        in_progress: 'bi bi-gear-fill',
        completed: 'bi bi-check-circle-fill',
        removed: 'bi bi-signpost-split-fill',
        rejected: 'bi bi-x-circle-fill',
        cancelled: 'bi bi-slash-circle-fill',
      }[status] || 'bi bi-circle-fill';
    },
    requestStatusClass(row) {
      return row?.status === 'completed' && row?.requestType === 'removal'
        ? 'removed'
        : row?.status;
    },
    requestStatusLabel(row) {
      if(this.requestStatusClass(row) === 'removed') return 'تمت إزالة اللوحة';
      return this.requestStatusChoices[row?.status] || row?.status;
    },
    bannerImage(row) {
      return row?.executionImage || this.bannerOf(row)?.image || null;
    },
    executionSheetAvailable(row) {
      return row?.service === 'banner' && ['approved', 'assigned', 'in_progress', 'completed'].includes(row?.status);
    },
    requestEndpoint(row) {
      return row?.service === 'banner' ? 'propertyBannerRequests' : 'propertyMarketingRequests';
    },
    async openExecutionSheet(row) {
      if(!this.executionSheetAvailable(row) || this.executionSheetLoadingId) return;
      const previewWindow = window.open('', '_blank');
      this.executionSheetLoadingId = row.id;
      this.hasError = false;
      try {
        const { data } = await this.$http.get(
          `/api/admin/propertyBannerRequests/executionSheet/${row.id}`,
          { responseType: 'blob' }
        );
        const pdfUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        if(previewWindow) {
          previewWindow.location.href = pdfUrl;
        } else {
          const link = document.createElement('a');
          link.href = pdfUrl;
          link.download = `property-banner-request-${row.id}.pdf`;
          link.click();
        }
        window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
      } catch(err) {
        if(previewWindow) previewWindow.close();
        if(err.response?.data instanceof Blob) {
          try {
            err.response.data = JSON.parse(await err.response.data.text());
          } catch(_) {
          }
        }
        this.setActionError(err);
      } finally {
        this.executionSheetLoadingId = null;
      }
    },
    adminSelectOptions() {
      return this.adminsList.reduce((options, admin) => {
        options[admin.id] = admin.name;
        return options;
      }, {});
    },
    canExecuteRow(row) {
      if(!this.workflowPermissions.canExecute) return false;
      return this.workflowPermissions.isSuperUser ||
        Number(row.assignedAdminId) === Number(this.workflowPermissions.currentAdminId);
    },
    canApproveRow(row) {
      if(!this.workflowPermissions.canApprove || !row) return false;
      return this.workflowPermissions.isSuperUser ||
        Number(row.approvalManagerId) === Number(this.workflowPermissions.currentAdminId);
    },
    hasSecondaryActions(row) {
      return this.canDelete ||
        (row?.status === 'pending_approval' && this.canUpdate) ||
        (['assigned', 'in_progress'].includes(row?.status) && this.canApproveRow(row));
    },
    async chooseAdmin(title) {
      if(!this.adminsList.length) {
        toast('لا يوجد موظفون متاحون للإسناد');
        return null;
      }
      const result = await this.$swal.fire({
        title,
        input: 'select',
        inputOptions: this.adminSelectOptions(),
        inputPlaceholder: 'اختر الموظف المسؤول',
        showCancelButton: true,
        confirmButtonText: 'تأكيد الإسناد',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#0a8f64',
        inputValidator: value => !value ? 'يجب اختيار الموظف المسؤول' : undefined,
      });
      return result.isConfirmed ? result.value : null;
    },
    async approveAndAssign(row) {
      if(!this.canApproveRow(row)) return;
      const approvalNote = await this.requestApprovalNote(row);
      if(approvalNote === null) return;
      const assignedAdminId = await this.chooseAdmin(`تعميد وإسناد ${this.orderNumber(row)}`);
      if(!assignedAdminId) return;
      this.actionLoadingId = row.id;
      this.hasError = false;
      try {
        await this.$http.put(`/api/admin/${this.requestEndpoint(row)}/update/${row.id}`, {
          status: 'approved',
          ...(approvalNote ? {approvalNote} : {}),
        });
        await this.$http.put(`/api/admin/${this.requestEndpoint(row)}/update/${row.id}`, {
          status: 'assigned',
          assignedAdminId,
        });
        toast('تم تعميد الطلب وإسناده بنجاح', 'success');
        await this.getList(false);
      } catch(err) {
        this.setActionError(err);
        await this.getList(false);
      } finally {
        this.actionLoadingId = null;
      }
    },
    async requestApprovalNote(row) {
      if(!['update', 'removal'].includes(row.requestType)) return '';
      const result = await this.$swal.fire({
        title: `ملاحظات تعميد ${this.orderNumber(row)}`,
        input: 'textarea',
        inputLabel: row.requestType === 'removal' ? 'ملاحظات تعميد إزالة اللوحة' : 'ملاحظات تعميد تحديث اللوحة',
        inputPlaceholder: 'اكتب ملاحظات التعميد...',
        inputValidator: value => !value || !value.trim() ? 'ملاحظات التعميد مطلوبة' : undefined,
        showCancelButton: true,
        confirmButtonText: 'متابعة التعميد',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#0a8f64',
      });
      return result.isConfirmed ? result.value.trim() : null;
    },
    async assignRequest(row) {
      if(!this.canApproveRow(row)) return;
      const assignedAdminId = await this.chooseAdmin(`إسناد ${this.orderNumber(row)}`);
      if(!assignedAdminId) return;
      await this.updateStatus(row, { status: 'assigned', assignedAdminId }, 'تم إسناد الطلب للموظف');
    },
    async reassignRequest(row) {
      if(!this.canApproveRow(row)) return;
      const assignedAdminId = await this.chooseAdmin(`تعديل إسناد ${this.orderNumber(row)}`);
      if(!assignedAdminId) return;
      await this.updateStatus(row, {
        status: row.status,
        assignedAdminId,
      }, 'تم تعديل مسؤول التنفيذ للطلب');
    },
    async deleteRequest(row) {
      if(!this.canDelete) return;
      const result = await this.$swal.fire({
        title: `حذف ${this.orderNumber(row)}؟`,
        text: 'سيختفي الطلب من القائمة مع الاحتفاظ بسجل الحذف وصور التنفيذ السابقة.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'حذف الطلب',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#dc3545',
      });
      if(!result.isConfirmed) return;
      this.actionLoadingId = row.id;
      try {
        await this.$http.delete(`/api/admin/${this.requestEndpoint(row)}/delete/${row.id}`);
        toast('تم حذف الطلب', 'success');
        await this.getList(false);
      } catch(err) {
        this.setActionError(err);
      } finally {
        this.actionLoadingId = null;
      }
    },
    async startRequest(row) {
      if(!this.canExecuteRow(row)) return;
      const result = await this.$swal.fire({
        title: 'بدء تنفيذ الطلب؟',
        text: `${this.orderNumber(row)} سيتم نقله إلى قيد التنفيذ`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'بدء التنفيذ',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#2874d0',
      });
      if(result.isConfirmed)
        await this.updateStatus(row, { status: 'in_progress' }, 'تم بدء تنفيذ الطلب');
    },
    async updateStatus(row, payload, successMessage) {
      this.actionLoadingId = row.id;
      this.hasError = false;
      try {
        await this.$http.put(`/api/admin/${this.requestEndpoint(row)}/update/${row.id}`, payload);
        toast(successMessage, 'success');
        await this.getList(false);
      } catch(err) {
        this.setActionError(err);
      } finally {
        this.actionLoadingId = null;
      }
    },
    setActionError(err) {
      [this.message, this.errorNumber] = this.$globalError(err, true);
      this.hasError = true;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    openCompletion(row) {
      if(!this.canExecuteRow(row)) return;
      if(row.service !== 'banner') {
        this.updateStatus(row, {status: 'completed'}, 'تم إكمال الطلب');
        return;
      }
      this.completionRequest = row;
      this.completionForm = {
        ...EMPTY_COMPLETION_FORM(),
        installedAt: this.today(),
        removedAt: this.today(),
      };
    },
    closeCompletion() {
      if(this.completionLoading) return;
      this.completionRequest = null;
      this.completionForm = EMPTY_COMPLETION_FORM();
      if(this.$refs.completionImage) this.$refs.completionImage.value = '';
    },
    setCompletionImage(event) {
      this.completionForm.image = event.target.files[0] || null;
    },
    async completeRequest() {
      if(!this.completionRequest || !this.canExecuteRow(this.completionRequest)) return;
      if(!this.completionForm.image) {
        toast('يجب رفع صورة تنفيذ تركيب أو تحديث أو إزالة اللوحة');
        return;
      }
      this.completionLoading = true;
      const formData = new FormData();
      formData.append('status', 'completed');
      if(this.completionRequest.requestType === 'removal') {
        formData.append('removedAt', this.completionForm.removedAt);
      } else {
        if(this.completionForm.bannerNumber) formData.append('bannerNumber', this.completionForm.bannerNumber);
        formData.append('installedAt', this.completionForm.installedAt);
      }
      formData.append('image', this.completionForm.image);
      try {
        await this.$http.put(`/api/admin/propertyBannerRequests/update/${this.completionRequest.id}`, formData);
        toast('تم إكمال الطلب وتحديث بيانات اللوحة', 'success');
        this.completionLoading = false;
        this.closeCompletion();
        await this.getList(false);
      } catch(err) {
        this.completionLoading = false;
        this.setActionError(err);
      }
    },
    today() {
      const date = new Date();
      const offset = date.getTimezoneOffset();
      return new Date(date.getTime() - offset * 60000).toISOString().split('T')[0];
    },
    openDetails(row) {
      this.detailsPropertyId = this.propertyOf(row)?.id || null;
    },
    closeDetails() {
      this.detailsPropertyId = null;
      this.getList(false);
    },
  },
  created() {
    this.getList();
  },
};
</script>

<style scoped>
.marketing-board-page {
  --board-green: #176548;
  --board-green-dark: #124f39;
  --board-ink: #14233a;
  --board-muted: #76869d;
  --board-line: #dce5ee;
  --board-line-soft: #e8eef3;
  --board-surface: #ffffff;
  --board-surface-soft: #f1f4f7;
  --board-surface-strong: #eef3f7;
  --board-hover: #fbfcfd;
  --board-shadow: 0 1px 3px rgba(15, 29, 48, 0.035);
  color: var(--board-ink);
}

.board-toolbar,
.requests-card {
  border: 1px solid var(--board-line);
  border-radius: 16px;
  background: var(--board-surface);
  box-shadow: var(--board-shadow);
  overflow: hidden;
}

.board-toolbar { margin-bottom: 24px; }
.toolbar-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  min-height: 80px;
  padding: 18px 22px 12px;
}

.page-heading {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.heading-icon { color: var(--board-green); font-size: 1.25rem; line-height: 1.4; }
.page-heading h4 { margin: 0 0 2px; font-size: 1.18rem; font-weight: 800; }
.page-heading p { margin: 0; color: var(--board-muted); font-size: 0.78rem; }

.service-filter {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 385px;
}
.new-request-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 42px;
  padding: 8px 14px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: var(--board-green);
  font-size: 0.78rem;
  font-weight: 800;
  white-space: nowrap;
}
.new-request-btn:hover { background: var(--board-green-dark); }
.service-filter label { color: var(--board-muted); font-size: 0.77rem; white-space: nowrap; }
.select-wrap { position: relative; flex: 1; }
.select-wrap .form-select {
  min-height: 42px;
  padding-left: 38px;
  border-color: var(--board-line);
  border-radius: 8px;
  background-color: var(--board-surface);
  background-image: none;
  color: var(--board-ink);
  font-size: 0.82rem;
  font-weight: 700;
}
.select-wrap > i {
  position: absolute;
  top: 50%;
  left: 13px;
  transform: translateY(-50%);
  pointer-events: none;
}

.board-filters {
  display: grid;
  grid-template-columns: minmax(180px, 260px) minmax(220px, 340px) auto;
  align-items: end;
  gap: 12px;
  padding: 14px 22px;
  border-top: 1px solid var(--board-line-soft);
  background: var(--board-surface-soft);
}
.filter-field label {
  display: block;
  margin-bottom: 5px;
  color: var(--board-muted);
  font-size: 0.72rem;
  font-weight: 700;
}
.filter-field .form-control {
  min-height: 40px;
  border-color: var(--board-line);
  color: var(--board-ink);
  background: var(--board-surface);
  font-size: 0.78rem;
}
.filter-actions { display: flex; gap: 7px; }

.status-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 22px 20px;
  border-top: 1px solid var(--board-line-soft);
  overflow-x: auto;
}
.status-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border: 0;
  border-radius: 9px;
  background: var(--board-surface-soft);
  color: var(--board-muted);
  font-size: 0.78rem;
  white-space: nowrap;
}
.status-tab.active { background: var(--board-green); color: #fff; font-weight: 700; }
.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 0.68rem;
  font-weight: 800;
}
.tab-count--warning { color: #fff; background: #f59e0b; }

.requests-card { min-height: 260px; }
.requests-table { width: 100%; min-width: 1120px; border-collapse: separate; border-spacing: 0; }
.requests-table th {
  padding: 13px 12px;
  border-bottom: 1px solid var(--board-line);
  background: var(--board-surface-strong);
  color: var(--board-ink);
  font-size: 0.73rem;
  font-weight: 800;
  text-align: right;
  vertical-align: middle;
}
.requests-table td {
  height: 74px;
  padding: 12px;
  border-bottom: 1px solid var(--board-line-soft);
  color: var(--board-ink);
  font-size: 0.78rem;
  vertical-align: middle;
}
.requests-table tbody tr:last-child td { border-bottom: 0; }
.requests-table tbody tr:hover { background: var(--board-hover); }
.requests-table td strong { display: block; font-size: 0.79rem; }
.requests-table td small {
  display: block;
  margin-top: 3px;
  color: var(--board-muted);
  font-size: 0.68rem;
}
.cell-subtitle { display: inline-block; margin-top: 2px; color: var(--board-muted); font-size: 0.7rem; font-weight: 700; }

.order-number {
  display: block;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--board-ink);
  direction: ltr;
  font-size: 0.73rem;
  font-weight: 800;
  text-align: right;
}
.order-number:hover { color: var(--board-green); text-decoration: underline; }
.order-cell { min-width: 112px; }
.service-name { display: flex; align-items: center; gap: 6px; }
.service-name i { font-size: 0.9rem; }
.license-number { display: block; direction: rtl; }
.location-link {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 4px;
  color: #2771d3;
  font-size: 0.7rem;
  font-weight: 700;
  text-decoration: none;
}
.location-link:hover { text-decoration: underline; }
.location-link--map { color: #087b5a; }
.location-unavailable {
  display: flex !important;
  align-items: center;
  gap: 4px;
  color: var(--board-muted) !important;
}

.request-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 9px;
  border: 1px solid transparent;
  border-radius: 16px;
  font-size: 0.7rem;
  font-weight: 800;
  white-space: nowrap;
}
.request-status--pending_approval { color: #8b5600; background: #fff6d8; border-color: #facb54; }
.request-status--approved { color: #087443; background: #e0f8ec; border-color: #8edbb5; }
.request-status--assigned { color: #5849bd; background: #efedff; border-color: #aaa0f6; }
.request-status--in_progress { color: #3546b5; background: #e9edff; border-color: #a8b2fb; }
.request-status--completed { color: #087443; background: #e0f8ec; border-color: #8edbb5; }
.request-status--removed { color: #566273; background: #edf1f4; border-color: #cad2da; }
.request-status--rejected { color: #b42318; background: #feeceb; border-color: #f4aaa4; }
.request-status--cancelled { color: #5f6c7c; background: #edf1f4; border-color: #cad2da; }

.action-cell { width: 180px; min-width: 180px; }
.action-stack {
  display: grid;
  gap: 7px;
  width: 100%;
}
.workflow-btn,
.details-btn,
.completed-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  min-height: 36px;
  padding: 7px 10px;
  border: 1px solid transparent;
  border-radius: 8px;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 800;
  line-height: 1.35;
  white-space: normal;
  text-decoration: none;
  transition: transform 0.16s ease, background-color 0.16s ease, border-color 0.16s ease;
}
.workflow-btn--warning { background: #e59400; border-color: #e59400; }
.workflow-btn--primary { background: #4059c7; border-color: #4059c7; }
.workflow-btn--success { background: #078d67; border-color: #078d67; }
.workflow-btn:hover { color: #fff; filter: brightness(0.96); transform: translateY(-1px); }
.details-btn { color: var(--board-ink); background: var(--board-surface-soft); }
.completed-link { color: #087953; border-color: #bce4d2; background: #edf9f3; }
.completed-link:hover { color: #076443; background: #e2f5ec; }
.completed-link--plain { cursor: default; }
.workflow-btn--pdf { color: #806024; background: #fffaf0; border-color: #d7bd8b; }
.workflow-btn--pdf:hover { color: #684c17; background: #fff4de; border-color: #c9a96b; }
.closed-request {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  width: 100%;
  padding: 8px;
  border: 1px solid var(--board-line-soft);
  border-radius: 8px;
  color: var(--board-muted);
  background: var(--board-surface-soft);
  font-size: 0.68rem;
  line-height: 1.5;
}
.action-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 48px;
  padding: 10px;
  border-radius: 8px;
  color: var(--board-muted);
  background: var(--board-surface-soft);
  font-size: 0.7rem;
}
.employee-line { margin-top: 0 !important; line-height: 1.55; }
.request-note { max-width: 230px; overflow-wrap: anywhere; }
.secondary-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(70px, 1fr));
  gap: 5px;
  padding-top: 1px;
}
.icon-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: auto;
  min-height: 31px;
  padding: 5px 7px;
  border: 1px solid var(--board-line);
  border-radius: 7px;
  background: var(--board-surface-soft);
  font-size: 0.64rem;
  font-weight: 700;
  line-height: 1.25;
  transition: background-color 0.16s ease, border-color 0.16s ease;
}
.icon-action--primary { color: #315acb; }
.icon-action--danger { color: #c62d27; }
.icon-action--primary:hover { border-color: #9bace9; background: #eef2ff; }
.icon-action--danger:hover { border-color: #efb1ad; background: #fff0ef; }

@media (max-width: 1440px) {
  .action-cell { width: 160px; min-width: 160px; padding-inline: 8px !important; }
  .action-stack { gap: 5px; }
  .workflow-btn,
  .details-btn,
  .completed-link {
    min-height: 32px;
    padding: 5px 7px;
    font-size: 0.64rem;
  }
  .secondary-actions { grid-template-columns: repeat(auto-fit, minmax(55px, 1fr)); }
  .icon-action { min-height: 29px; padding: 4px 5px; font-size: 0.59rem; }
}

.empty-state { padding: 62px 20px; text-align: center; }
.empty-state > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  margin-bottom: 12px;
  border-radius: 18px;
  color: var(--board-green);
  background: var(--board-surface-soft);
  font-size: 1.5rem;
}
.empty-state h5 { margin-bottom: 5px; font-weight: 800; }
.empty-state p { margin: 0; color: var(--board-muted); font-size: 0.8rem; }

.board-pagination { display: flex; justify-content: center; gap: 6px; margin: 20px 0; }
.board-pagination button {
  min-width: 34px;
  height: 34px;
  border: 1px solid var(--board-line);
  border-radius: 8px;
  color: var(--board-ink);
  background: var(--board-surface);
}
.board-pagination button.active { color: #fff; border-color: var(--board-green); background: var(--board-green); }
.board-pagination button:disabled { opacity: 0.45; }

.board-modal-backdrop,
.details-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1080;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 29, 48, 0.48);
}
.board-modal {
  width: min(620px, 100%);
  padding: 22px;
  border: 1px solid var(--board-line);
  border-radius: 16px;
  color: var(--board-ink);
  background: var(--board-surface);
  box-shadow: 0 20px 60px rgba(15, 29, 48, 0.2);
}
.board-modal__header,
.details-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--board-line-soft);
}
.board-modal__header h5,
.details-panel__header h5 { margin: 0 0 4px; font-weight: 800; }
.board-modal__header p,
.details-panel__header p { margin: 0; color: var(--board-muted); font-size: 0.75rem; }
.board-modal__actions { display: flex; justify-content: flex-end; gap: 8px; padding-top: 8px; }

.details-backdrop { justify-content: flex-start; padding: 0; }
.details-panel {
  width: min(920px, 94vw);
  height: 100vh;
  color: var(--board-ink);
  background: var(--board-surface);
  box-shadow: 18px 0 60px rgba(15, 29, 48, 0.22);
  overflow: hidden;
}
.details-panel__header { margin: 0; padding: 20px 24px; }
.details-panel__body { height: calc(100vh - 88px); padding: 22px; overflow-y: auto; }

@media (max-width: 991.98px) {
  .toolbar-top { align-items: stretch; flex-direction: column; }
  .service-filter { width: 100%; min-width: 0; }
  .board-filters { grid-template-columns: 1fr 1fr; }
  .filter-actions { grid-column: 1 / -1; }
}

@media (max-width: 575.98px) {
  .toolbar-top { padding: 16px; }
  .page-heading h4 { font-size: 1rem; }
  .service-filter { align-items: stretch; flex-direction: column; gap: 6px; }
  .board-filters { grid-template-columns: 1fr; padding: 14px 16px; }
  .filter-actions { grid-column: auto; }
  .status-tabs { padding: 8px 16px 16px; }
  .board-modal-backdrop { padding: 10px; }
  .board-modal { padding: 18px; }
}
</style>

<style>
html[data-bs-theme="dark"] .marketing-board-page {
  --board-green: #92bd79;
  --board-green-dark: #7aa364;
  --board-ink: #e2e9de;
  --board-muted: #9ba797;
  --board-line: rgba(166, 144, 87, 0.24);
  --board-line-soft: rgba(166, 144, 87, 0.14);
  --board-surface: #20281e;
  --board-surface-soft: #293326;
  --board-surface-strong: #252f22;
  --board-hover: rgba(146, 189, 121, 0.07);
  --board-shadow: 0 12px 30px rgba(0, 0, 0, 0.16);
  color-scheme: dark;
}

html[data-bs-theme="dark"] .marketing-board-page .app-content-header h3,
html[data-bs-theme="dark"] .marketing-board-page .page-heading h4,
html[data-bs-theme="dark"] .marketing-board-page .empty-state h5,
html[data-bs-theme="dark"] .marketing-board-page .board-modal__header h5,
html[data-bs-theme="dark"] .marketing-board-page .details-panel__header h5 {
  color: var(--board-ink);
}

html[data-bs-theme="dark"] .marketing-board-page .breadcrumb-item,
html[data-bs-theme="dark"] .marketing-board-page .breadcrumb-item.active,
html[data-bs-theme="dark"] .marketing-board-page .text-muted {
  color: var(--board-muted) !important;
}

html[data-bs-theme="dark"] .marketing-board-page .breadcrumb-item a,
html[data-bs-theme="dark"] .marketing-board-page .location-link {
  color: #8eb8e9;
}

html[data-bs-theme="dark"] .marketing-board-page .location-link--map {
  color: #8dd4ac;
}

html[data-bs-theme="dark"] .marketing-board-page .select-wrap .form-select {
  color: var(--board-ink);
  background-color: var(--board-surface-soft);
}

html[data-bs-theme="dark"] .marketing-board-page .filter-field .form-control {
  color: var(--board-ink);
  border-color: var(--board-line);
  background: var(--board-surface);
}

html[data-bs-theme="dark"] .marketing-board-page .icon-action {
  border-color: var(--board-line);
  background: var(--board-surface-soft);
}

html[data-bs-theme="dark"] .marketing-board-page .icon-action--primary {
  color: #aabcf8;
}

html[data-bs-theme="dark"] .marketing-board-page .icon-action--danger {
  color: #f1a29b;
}

html[data-bs-theme="dark"] .marketing-board-page .icon-action--primary:hover {
  border-color: #596b9c;
  background: #303b55;
}

html[data-bs-theme="dark"] .marketing-board-page .icon-action--danger:hover {
  border-color: #774a45;
  background: #4a302d;
}

html[data-bs-theme="dark"] .marketing-board-page .workflow-btn--pdf {
  color: #e5c887;
  border-color: #715f39;
  background: #393424;
}

html[data-bs-theme="dark"] .marketing-board-page .workflow-btn--pdf:hover {
  color: #f1d89e;
  border-color: #8a7444;
  background: #453d28;
}

html[data-bs-theme="dark"] .marketing-board-page .completed-link {
  color: #9cdbb9;
  border-color: #376b51;
  background: #1f4735;
}

html[data-bs-theme="dark"] .marketing-board-page .status-tab:not(.active):hover,
html[data-bs-theme="dark"] .marketing-board-page .details-btn:hover,
html[data-bs-theme="dark"] .marketing-board-page .board-pagination button:not(.active):not(:disabled):hover {
  color: var(--board-ink);
  background: #343f30;
}

html[data-bs-theme="dark"] .marketing-board-page .request-status--pending_approval {
  color: #f0ce77;
  background: #493b19;
  border-color: #756029;
}
html[data-bs-theme="dark"] .marketing-board-page .request-status--approved,
html[data-bs-theme="dark"] .marketing-board-page .request-status--completed {
  color: #9cdbb9;
  background: #1f4735;
  border-color: #376b51;
}
html[data-bs-theme="dark"] .marketing-board-page .request-status--assigned {
  color: #c1b8ff;
  background: #38345f;
  border-color: #565083;
}
html[data-bs-theme="dark"] .marketing-board-page .request-status--in_progress {
  color: #a5c5fa;
  background: #263e63;
  border-color: #3f5e8b;
}
html[data-bs-theme="dark"] .marketing-board-page .request-status--rejected {
  color: #f2a198;
  background: #552b27;
  border-color: #79423c;
}
html[data-bs-theme="dark"] .marketing-board-page .request-status--cancelled {
  color: #c2cbc0;
  background: #384036;
  border-color: #50594d;
}
html[data-bs-theme="dark"] .marketing-board-page .request-status--removed {
  color: #cbd4c8;
  background: #343d32;
  border-color: #566052;
}

html[data-bs-theme="dark"] .marketing-board-page .service-name .text-success,
html[data-bs-theme="dark"] .marketing-board-page .completed-link {
  color: #8dd4ac !important;
}
html[data-bs-theme="dark"] .marketing-board-page .service-name .text-danger {
  color: #f0a098 !important;
}

html[data-bs-theme="dark"] .marketing-board-page .board-modal-backdrop,
html[data-bs-theme="dark"] .marketing-board-page .details-backdrop {
  background: rgba(7, 11, 6, 0.72);
}

html[data-bs-theme="dark"] .marketing-board-page .board-modal .btn-light {
  color: var(--board-ink);
  border-color: var(--board-line);
  background: var(--board-surface-soft);
}

html[data-bs-theme="dark"] .marketing-board-page .board-modal .btn-light:hover {
  background: #354131;
}
</style>
